// What a ticket was raised from (spec 2026-10-11-26): the documents the
// ticket's "Source document" card lists, and the label naming its target.
// Pure, so the rules are unit-tested without rendering.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export interface IssueSourceInput {
  target_entity_type?: string | null
  target_entity_uid?: string | null
  properties?: unknown
}

export interface ContractSourceInput {
  source_document_uid?: string | null
}

const asUuid = (value: unknown): string | undefined =>
  typeof value === 'string' && UUID_RE.test(value) ? value : undefined

/**
 * The documents a ticket stems from, deduplicated, in this order: the document
 * it targets; for a contract target, the contract's source document (nothing
 * else from the contract); `properties.document_uid`; `properties.commitment_uid`
 * when the commitment is a quote (a quote is a document). Photos, voice notes
 * and dossier pieces are evidence shown by their own cards, never read here.
 */
export function issueSourceDocumentUids(
  issue: IssueSourceInput,
  contract?: ContractSourceInput | null,
): string[] {
  const props =
    issue.properties && typeof issue.properties === 'object'
      ? (issue.properties as Record<string, unknown>)
      : {}
  const candidates = [
    issue.target_entity_type === 'document' ? issue.target_entity_uid : undefined,
    issue.target_entity_type === 'contract' ? contract?.source_document_uid : undefined,
    props.document_uid,
    props.commitment_kind === 'quote' ? props.commitment_uid : undefined,
  ]
  const out: string[] = []
  for (const candidate of candidates) {
    const uid = asUuid(candidate)
    if (uid && !out.includes(uid)) out.push(uid)
  }
  return out
}

/**
 * The label above a ticket's target: the target's own type name when known
 * (« Immeuble », « Lot », « Règlement de copropriété »), else the target
 * entity type, else a generic "Linked item".
 */
export function issueTargetLabel(
  type: string | null | undefined,
  typeName: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (typeName) return typeName
  const other = t('targetEntityType.other')
  if (!type) return other
  return t(`targetEntityType.${type}`, { defaultValue: other })
}
